/**
 * Canonical tab / sub-tab indices and query keys for candidate, job, and client
 * detail pages. Keep deep links and useDetailTabs configs aligned with these.
 */

export const CANDIDATE_TAB = {
  general: 0,
  interviews: 1,
  outreach: 2,
  /** Tasks tab (index preserved from former Reminders tab for deep links). */
  tasks: 3,
  history: 4,
} as const;

export const CANDIDATE_GENERAL_SUBTAB = {
  jobs: 0,
  notes: 1,
  workHistory: 2,
  attachments: 3,
} as const;

export const CANDIDATE_OUTREACH_SUBTAB = {
  sequences: 0,
  emails: 1,
} as const;

export const CANDIDATE_INTERVIEWS_SUBTAB = {
  interviews: 0,
  other: 1,
} as const;

export const JOB_TAB = {
  general: 0,
  recordings: 1,
  publicLink: 2,
  history: 3,
} as const;

export const JOB_GENERAL_SUBTAB = {
  candidates: 0,
  currentPipeline: 1,
  cumulativePipeline: 2,
  description: 3,
  notes: 4,
  attachments: 5,
} as const;

export const CLIENT_TAB = {
  general: 0,
  recordings: 1,
  alerts: 2,
  history: 3,
} as const;

export const CLIENT_GENERAL_SUBTAB = {
  jobs: 0,
  contacts: 1,
  notes: 2,
  attachments: 3,
  invoices: 4,
  contracts: 5,
} as const;

export const CLIENT_ALERT_SUBTAB = {
  news: 0,
  jobAlerts: 1,
} as const;

export const DEMO_CALL_TAB = {
  details: 0,
  recordings: 1,
  notes: 2,
} as const;

export const DETAIL_TAB_QUERY_KEYS = {
  candidate: {
    tab: 'tab',
    generalSubTab: 'generalSubTab',
    outreachSubTab: 'outreachSubTab',
    interviewsSubTab: 'interviewsSubTab',
  },
  job: {
    tab: 'jobTab',
    generalSubTab: 'jobSubTab',
  },
  client: {
    tab: 'clientTab',
    generalSubTab: 'clientSubTab',
    alertSubTab: 'clientAlertSubTab',
  },
  demoCall: {
    tab: 'demoCallTab',
  },
} as const;

/** Query values that open the Notes thread (used by Copy link + feed deep links). */
export const NOTE_DEEP_LINK_QUERY = {
  candidate: {
    [DETAIL_TAB_QUERY_KEYS.candidate.tab]: String(CANDIDATE_TAB.general),
    [DETAIL_TAB_QUERY_KEYS.candidate.generalSubTab]: String(CANDIDATE_GENERAL_SUBTAB.notes),
  },
  job: {
    [DETAIL_TAB_QUERY_KEYS.job.tab]: String(JOB_TAB.general),
    [DETAIL_TAB_QUERY_KEYS.job.generalSubTab]: String(JOB_GENERAL_SUBTAB.notes),
  },
  client: {
    [DETAIL_TAB_QUERY_KEYS.client.tab]: String(CLIENT_TAB.general),
    [DETAIL_TAB_QUERY_KEYS.client.generalSubTab]: String(CLIENT_GENERAL_SUBTAB.notes),
  },
  task: {},
  demoCall: {
    [DETAIL_TAB_QUERY_KEYS.demoCall.tab]: String(DEMO_CALL_TAB.notes),
  },
} as const;

const buildHref = (path: string, params: Record<string, string | number | undefined | null>) => {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') {
      continue;
    }

    search.set(key, String(value));
  }

  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

export const candidateHref = (id: string, params: Record<string, string | number | undefined | null> = {}) =>
  buildHref('/candidate', { id, ...params });

export const jobHref = (id: string, params: Record<string, string | number | undefined | null> = {}) =>
  buildHref('/job', { id, ...params });

export const clientHref = (id: string, params: Record<string, string | number | undefined | null> = {}) =>
  buildHref('/client', { id, ...params });

export const taskHref = (id: string, params: Record<string, string | number | undefined | null> = {}) =>
  buildHref('/task', { id, ...params });

export const demoCallHref = (id: string, params: Record<string, string | number | undefined | null> = {}) =>
  buildHref('/demo-call', { id, ...params });

export const candidateNotesHref = (id: string, noteId: string) =>
  candidateHref(id, { ...NOTE_DEEP_LINK_QUERY.candidate, noteId });

export const jobNotesHref = (id: string, noteId: string) =>
  jobHref(id, { ...NOTE_DEEP_LINK_QUERY.job, noteId });

export const clientNotesHref = (id: string, noteId: string) =>
  clientHref(id, { ...NOTE_DEEP_LINK_QUERY.client, noteId });

export const taskNotesHref = (id: string, noteId: string) =>
  taskHref(id, { ...NOTE_DEEP_LINK_QUERY.task, noteId });

export const demoCallNotesHref = (id: string, noteId: string) =>
  demoCallHref(id, { ...NOTE_DEEP_LINK_QUERY.demoCall, noteId });

export const candidateInterviewsHref = (id: string, interviewId?: string) =>
  candidateHref(id, {
    [DETAIL_TAB_QUERY_KEYS.candidate.tab]: CANDIDATE_TAB.interviews,
    [DETAIL_TAB_QUERY_KEYS.candidate.interviewsSubTab]: CANDIDATE_INTERVIEWS_SUBTAB.interviews,
    ...(interviewId ? { interviewId } : {}),
  });

export const candidateOtherRecordingsHref = (id: string) =>
  candidateHref(id, {
    [DETAIL_TAB_QUERY_KEYS.candidate.tab]: CANDIDATE_TAB.interviews,
    [DETAIL_TAB_QUERY_KEYS.candidate.interviewsSubTab]: CANDIDATE_INTERVIEWS_SUBTAB.other,
  });

export const clientRecordingsHref = (id: string) =>
  clientHref(id, {
    [DETAIL_TAB_QUERY_KEYS.client.tab]: CLIENT_TAB.recordings,
  });

export const jobRecordingsHref = (id: string) =>
  jobHref(id, {
    [DETAIL_TAB_QUERY_KEYS.job.tab]: JOB_TAB.recordings,
  });

export const candidateTasksHref = (id: string, taskId?: string) =>
  candidateHref(id, {
    [DETAIL_TAB_QUERY_KEYS.candidate.tab]: CANDIDATE_TAB.tasks,
    ...(taskId ? { taskId } : {}),
  });

export const clientContactsHref = (id: string, contactId?: string) =>
  clientHref(id, {
    [DETAIL_TAB_QUERY_KEYS.client.tab]: CLIENT_TAB.general,
    [DETAIL_TAB_QUERY_KEYS.client.generalSubTab]: CLIENT_GENERAL_SUBTAB.contacts,
    ...(contactId ? { contactId } : {}),
  });

export const clientJobsHref = (id: string) =>
  clientHref(id, {
    [DETAIL_TAB_QUERY_KEYS.client.tab]: CLIENT_TAB.general,
    [DETAIL_TAB_QUERY_KEYS.client.generalSubTab]: CLIENT_GENERAL_SUBTAB.jobs,
  });
